import { useAxios } from '@monofront/vue-axios-plugin'
import { HeadAttrs } from '@vueuse/head'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getApiUrl } from '@/config/app'
import { getLanguageDefault } from '@/config/languages'
import { logger } from '@/utils/logger'

import type { AxiosResponse } from '@monofront/vue-axios-plugin'
import type { Language } from '@/config/languages'
import type { Cta, Data, Picture } from '@/types'

interface NavLink {
  label: string
  url: string
  target?: string
}

export interface SocialLink {
  type: string
  url: string
}

export interface Address {
  name: string
  street: string
  number: string
  zipcode: string
  city: string
  country: string
  phone: Data
  gmapsUrl: string
}

export type Chrome = {
  address: Address
  cta: Cta
  socials: SocialLink[]
  legals: NavLink[]
  main: NavLink[]
  secondary: NavLink[]
  footer: NavLink[]
  language: Language
  script: HeadAttrs[]
  i18n: Record<string, string>
  noise: Picture | null
  paperFold: Picture | null
  mailchimpCountries: {
    fr?: Record<string, string>
    en?: Record<string, string>
  }
  newsletter?: {
    picture: Picture
    title: string
    htmltext: string
    btnLabel: string
  }
}

const languageDefault = getLanguageDefault()

export const useChromeStore = defineStore('chrome', () => {
  const axios = useAxios()

  const isNewsletterVisible = ref(false)
  const address = ref<Chrome['address']>({} as Address)
  const cta = ref<Chrome['cta']>({} as Cta)
  const socials = ref<Chrome['socials']>([])
  const legals = ref<Chrome['legals']>([])
  const main = ref<Chrome['main']>([])
  const secondary = ref<Chrome['secondary']>([])
  const footer = ref<Chrome['footer']>([])
  const script = ref<Chrome['script']>([])
  const i18n = ref<Chrome['i18n']>({})
  const mailchimpCountries = ref<Chrome['mailchimpCountries']>({})
  const newsletter = ref<Chrome['newsletter']>()

  const noise = ref<Chrome['noise']>(null)
  const paperFold = ref<Chrome['paperFold']>(null)

  const language = ref<Language>(languageDefault)

  // Getters
  const t = computed(() => (id: string) => i18n.value[id] || id)

  // Actions
  async function fetchChrome(l: Language = languageDefault) {
    try {
      const response = (await axios.get(
        `${getApiUrl()}/chrome`
      )) as AxiosResponse<Chrome>
      logger.info(
        '[fetchChrome] fetched !',
        response.request.responseURL || response.request.res.responseUrl // client vs SSR
      )
      const data: Chrome = response?.data

      language.value = l

      address.value = data.address
      cta.value = data.cta
      socials.value = data.socials
      legals.value = data.legals
      main.value = data.main
      secondary.value = data.secondary
      footer.value = data.footer
      script.value = data.script
      i18n.value = data.i18n
      noise.value = data.noise
      paperFold.value = data.paperFold
      newsletter.value = data.newsletter
    } catch (error) {
      logger.error('[fetchChrome]', error)
    }
  }

  async function openNewsletterPopup() {
    // Charge une seule fois, à l'ouverture de la popup, la liste de pays mailchimp dans la langue courante
    if (!mailchimpCountries.value[language.value]) {
      mailchimpCountries.value[language.value] = (
        await import(`@/i18n/mailchimp-countries.${language.value}.json`)
      ).default
    }

    isNewsletterVisible.value = true

    setTimeout(() => {
      document.documentElement.classList.add('no-scroll')
    }, 100)
  }

  return {
    isNewsletterVisible,
    address,
    cta,
    socials,
    legals,
    main,
    secondary,
    footer,
    script,
    i18n,
    noise,
    paperFold,
    language,
    t,
    mailchimpCountries,
    fetchChrome,
    openNewsletterPopup,
    newsletter,
  }
})
